/**
 * Geocode typed address text to a place we can ship to (US or Canada).
 *
 * The Places Autocomplete takes a LIST of countries; the Geocoder takes ONE.
 * Both address forms passed the Geocoder `country: ['US', 'CA']`, so every
 * call threw "in property componentRestrictions: in property country: not a
 * string" -- the fallback for a typed, pasted or autofilled address, or for a
 * suggestion Google can no longer look up, never worked once. Found
 * 2026-10-01 when "600 Congress Ave., Austin" (a suggestion whose place id
 * answers NOT_FOUND) left the seller address form with no Save button.
 *
 * So no restriction: bias to the US, then keep the first result in a country
 * we ship to.
 */
export const GEOCODE_COUNTRIES = ['US', 'CA'] as const;

export interface GeocodeComponent {
  types: string[];
  long_name: string;
  short_name: string;
}

export interface GeocodeResult {
  address_components: GeocodeComponent[];
  geometry?: { location: { lat: () => number; lng: () => number } };
}

export interface GeocoderLike {
  geocode: (request: { address: string; region?: string }) => Promise<{ results?: GeocodeResult[] }>;
}

/**
 * The first result in the US or Canada, or null when there is none. Rejects
 * as the Geocoder does: ZERO_RESULTS for text it cannot place, and other codes
 * for quota or key trouble.
 */
export async function geocodeShippingAddress(geocoder: GeocoderLike, address: string): Promise<GeocodeResult | null> {
  const { results } = await geocoder.geocode({ address, region: 'us' });
  return (
    results?.find((r) =>
      r.address_components?.some(
        (c) => c.types.includes('country') && (GEOCODE_COUNTRIES as readonly string[]).includes(c.short_name),
      ),
    ) ?? null
  );
}
